import { createError } from '@common/neo.tax';

import {
  useCursorPaginatedQuery,
  GET_ASC_WAGES,
  Conditions,
  FilterField,
  advancedFilter,
  keywordFilter,
  GetAscWagesQuery,
  AscWageFilter,
  AscWageSort,
  GetAscWagesQueryVariables,
  useSticky,
} from '@neo.tax/shared';

import { useAscContext } from '../context';

export type AscWage = NonNullable<GetAscWagesQuery['getAscWages']>['edges'][number]['node'];

const GET_ASC_WAGES_KEY = 'GET_ASC_WAGES_KEY';

interface UseAscWagesProps {
  filterConditions?: Conditions<FilterField<AscWageFilter>>;
  searchKeyword?: string | null;
  sorting?: Array<AscWageSort> | AscWageSort;
}

export const useAscWages = ({ filterConditions, searchKeyword, sorting }: UseAscWagesProps = {}): {
  loading: boolean;
  ascWages: AscWage[] | null;
  hasNextPage: boolean;
  onLoadMoreItems: () => Promise<void>;
  isLoadingMore: boolean;
} => {
  const { fiscalYearId, periodName } = useAscContext();

  const {
    data: requestData,
    error,
    hasNextPage,
    isLoadingMore,
    loading,
    onLoadMoreItems,
  } = useCursorPaginatedQuery<GetAscWagesQuery, GetAscWagesQueryVariables>(
    GET_ASC_WAGES,
    {
      variables: {
        filter: {
          and: [
            keywordFilter(['name', 'titles', 'departments'], searchKeyword),
            advancedFilter<AscWageFilter>(filterConditions),
          ],
        },
        sorting,
        fiscalYearId,
        periodName,
      },
      context: {
        debounceKey: GET_ASC_WAGES_KEY,
      },
    },
    {
      getConnection: (data) => data.getAscWages,
      numElementsPerFetch: 40,
    },
  );

  // Keep existing data on the screen while a new query is running, so it
  // doesn't keep clearing the screen as you type into the filter.
  const stickyData = useSticky(requestData);
  const ascWages = stickyData?.getAscWages?.edges.map((edge) => edge.node) ?? null;

  if (loading) {
    return {
      loading: true,
      ascWages,
      hasNextPage: false,
      onLoadMoreItems: () => Promise.resolve(),
      isLoadingMore,
    };
  }
  if (error) {
    throw error;
  }

  if (!stickyData) {
    throw createError('MissingAscWages', 'No data returned from getAscWages query');
  }

  return {
    loading,
    ascWages,
    hasNextPage,
    onLoadMoreItems,
    isLoadingMore,
  };
};
